<template>
	<v-dialog v-model="dialog" max-width="520" persistent scrollable>
		<v-card class="complaint-modal">
			<div class="complaint-header">
				<div class="complaint-header-content">
					<div class="complaint-icon-wrap">
						<v-icon color="white" size="18">mdi-shield-alert-outline</v-icon>
					</div>
					<div>
						<div class="complaint-title">Canal de denuncias</div>
						<div class="complaint-subtitle">
							<v-icon size="12" color="rgba(255,255,255,0.78)" class="mr-1">mdi-lock-outline</v-icon>
							Confidencial y sin represalias
						</div>
					</div>
				</div>
				<v-btn icon small class="complaint-close" :disabled="loading" @click="close">
					<v-icon color="rgba(255,255,255,0.8)" size="18">mdi-close</v-icon>
				</v-btn>
			</div>

			<v-card-text class="complaint-body">
				<v-form ref="form" v-model="valid" @submit.prevent="submitComplaint">
					<v-select
						v-model="form.type"
						:items="complaintTypes"
						:rules="typeRules"
						label="Tipo de denuncia"
						outlined
						dense
						required
						prepend-inner-icon="mdi-format-list-bulleted"
					></v-select>

					<v-textarea
						v-model="form.description"
						:rules="descriptionRules"
						label="Descripción de los hechos"
						hint="¿Qué ocurrió? ¿Cuándo, dónde y quiénes podrían estar involucrados?"
						persistent-hint
						outlined
						dense
						rows="4"
						auto-grow
						counter="10000"
						required
						class="mt-1"
					></v-textarea>

					<div class="anon-row mt-4" :class="{ 'anon-row--active': form.isAnonymous }">
						<div class="anon-row-text">
							<div class="anon-row-title">
								<v-icon size="16" :color="form.isAnonymous ? '#2D4B95' : '#8A93A6'" class="mr-1">
									{{ form.isAnonymous ? 'mdi-incognito' : 'mdi-account-outline' }}
								</v-icon>
								{{ form.isAnonymous ? 'Denuncia anónima' : 'Denuncia NO anónima' }}
							</div>
							<div class="anon-row-hint">
								{{ form.isAnonymous ? 'No se asociará esta denuncia con tu usuario.' : 'Tu denuncia quedará asociada a tu usuario.' }}
							</div>
						</div>
						<v-switch
							v-model="form.isAnonymous"
							color="#2D4B95"
							dense
							hide-details
							class="mt-0 pt-0"
						></v-switch>
					</div>
				</v-form>
			</v-card-text>

			<v-card-actions class="complaint-footer">
				<v-btn text small color="#2D4B95" :disabled="loading" @click="close">
					Cancelar
				</v-btn>
				<v-spacer></v-spacer>
				<v-btn
					class="complaint-submit-btn"
					color="#2D4B95"
					small
					:dark="valid"
					:loading="loading"
					:disabled="!valid"
					@click="submitComplaint"
				>
					<v-icon left small>mdi-send-outline</v-icon>
					Enviar denuncia
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import { createComplaint } from '@/helpers/api/complaints'

export default {
	name: 'ComplaintModal',
	props: {
		value: {
			type: Boolean,
			default: false
		}
	},
	data: () => ({
		valid: false,
		loading: false,
		complaintTypes: [
			'Cohecho',
			'Lavado de activos',
			'Fraude',
			'Administración desleal',
			'Delito tributario',
			'Delito informático',
			'Conflicto de interés',
			'Incumplimiento del Código de Ética',
			'Uso indebido de datos personales',
			'Otra conducta'
		],
		form: {
			type: null,
			description: '',
			isAnonymous: false
		}
	}),
	computed: {
		dialog: {
			get() {
				return this.value
			},
			set(value) {
				this.$emit('input', value)
			}
		},
		typeRules() {
			return [value => !!value || 'Selecciona un tipo de denuncia.']
		},
		descriptionRules() {
			return [
				value => !!value || 'Describe los hechos de la denuncia.',
				value => (value && value.length <= 10000) || 'La descripción no puede superar los 10.000 caracteres.'
			]
		}
	},
	watch: {
		value(value) {
			if (value) {
				this.resetForm()
			}
		}
	},
	methods: {
		close() {
			if (this.loading) return
			this.dialog = false
		},
		resetForm() {
			this.form = {
				type: null,
				description: '',
				isAnonymous: false
			}
			this.valid = false
			if (this.$refs.form) {
				this.$refs.form.resetValidation()
			}
		},
		async submitComplaint() {
			if (!this.$refs.form.validate()) return

			this.loading = true
			try {
				const response = await createComplaint({
					tipo: this.form.type,
					descripcion: this.form.description,
					anonimo: this.form.isAnonymous
				})

				if (response && (response.code === 201 || response.code === 200)) {
					this.$store.globalStore.dispatch('addMessage', {
						title: 'Denuncia enviada',
						message: 'Tu denuncia fue recibida correctamente.',
						created: new Date(),
						type: 'success',
						icon: 'mdi-check-circle',
						color: '#3EBFA3'
					})
					this.dialog = false
				}
			} catch (error) {
				this.$store.globalStore.dispatch('addMessage', {
					title: 'No se pudo enviar la denuncia',
					message: 'Ocurrió un error. Intenta nuevamente.',
					created: new Date(),
					type: 'error',
					icon: 'mdi-alert-circle',
					color: '#F4516C'
				})
			} finally {
				this.loading = false
			}
		}
	}
}
</script>

<style scoped>
.complaint-modal {
	border-radius: 12px !important;
	overflow: hidden;
}
.complaint-header {
	align-items: center;
	background: linear-gradient(135deg, #2D4B95 0%, #5E85EF 100%);
	display: flex;
	justify-content: space-between;
	padding: 14px 18px;
}
.complaint-header-content {
	align-items: center;
	display: flex;
}
.complaint-icon-wrap {
	align-items: center;
	background: rgba(255,255,255,0.16);
	border-radius: 8px;
	display: flex;
	height: 32px;
	justify-content: center;
	margin-right: 10px;
	width: 32px;
	flex-shrink: 0;
}
.complaint-title {
	color: white;
	font-size: 16px;
	font-weight: 700;
	line-height: 1.2;
}
.complaint-subtitle {
	align-items: center;
	color: rgba(255,255,255,0.78);
	display: flex;
	font-size: 11.5px;
	margin-top: 2px;
}
.complaint-close {
	margin-right: -6px;
}
.complaint-body {
	padding: 20px 20px 6px !important;
}

.anon-row {
	align-items: center;
	background: #F5F7FB;
	border: 1px solid #E8EBF3;
	border-radius: 10px;
	display: flex;
	justify-content: space-between;
	padding: 10px 14px;
	transition: background 0.15s ease, border-color 0.15s ease;
}
.anon-row--active {
	background: #EEF2FC;
	border-color: #C9D6F5;
}
.anon-row-text {
	padding-right: 12px;
}
.anon-row-title {
	align-items: center;
	color: #2A2E3A;
	display: flex;
	font-size: 13.5px;
	font-weight: 600;
}
.anon-row-hint {
	color: #7A8296;
	font-size: 11.5px;
	margin-top: 2px;
}

.complaint-footer {
	border-top: 1px solid #e8ebf3;
	padding: 12px 20px !important;
}

.complaint-submit-btn.v-btn--disabled {
	background-color: #DCE1EC !important;
	opacity: 1 !important;
}
.complaint-submit-btn.v-btn--disabled ::v-deep .v-btn__content {
	color: #97A0B5 !important;
}
</style>